import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, MoreHorizontal, Pencil, Trash2, Eye, Star, StarOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency, propertyTypeLabels, transactionTypeLabels, statusLabels, getPropertyImage } from '../../lib/utils';

export default function AdminProperties() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    property_type: '',
    transaction_type: '',
    status: ''
  });

  const fetchProperties = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.property_type && filters.property_type !== 'all') params.append('property_type', filters.property_type);
      if (filters.transaction_type && filters.transaction_type !== 'all') params.append('transaction_type', filters.transaction_type);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (search) params.append('neighborhood', search);
      
      const response = await api.get(`/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Erro ao carregar imóveis.');
    } finally {
      setLoading(false);
    }
  }, [api, filters, search]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success('Imóvel removido com sucesso.');
      fetchProperties();
    } catch (error) {
      toast.error('Erro ao remover imóvel.');
    }
  };

  const toggleFeatured = async (property) => {
    try {
      await api.put(`/properties/${property.id}`, {
        ...property,
        featured: !property.featured
      });
      toast.success(property.featured ? 'Destaque removido.' : 'Imóvel destacado!');
      fetchProperties();
    } catch (error) {
      toast.error('Erro ao atualizar imóvel.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'sold': return 'bg-blue-100 text-blue-700';
      case 'rented': return 'bg-purple-100 text-purple-700';
      case 'inactive': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-properties">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">Imóveis</h1>
          <p className="text-slate-500 mt-1">{properties.length} imóveis cadastrados</p>
        </div>
        <Button asChild className="bg-brand-blue hover:bg-brand-blue-dark">
          <Link to="/admin/properties/new" data-testid="new-property-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Imóvel
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por bairro ou título..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="property-search"
                />
              </div>
            </div>
            <Select value={filters.property_type} onValueChange={(v) => setFilters({ ...filters, property_type: v })}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="house">Casa</SelectItem>
                <SelectItem value="apartment">Apartamento</SelectItem>
                <SelectItem value="land">Terreno</SelectItem>
                <SelectItem value="commercial">Comercial</SelectItem>
                <SelectItem value="rural">Rural</SelectItem>
                <SelectItem value="chacara">Chácara</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.transaction_type} onValueChange={(v) => setFilters({ ...filters, transaction_type: v })}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Transação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="sale">Venda</SelectItem>
                <SelectItem value="rent">Aluguel</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="available">Disponível</SelectItem>
                <SelectItem value="sold">Vendido</SelectItem>
                <SelectItem value="rented">Alugado</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Properties List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      ) : properties.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-slate-500 mb-4">Nenhum imóvel encontrado.</p>
            <Button asChild>
              <Link to="/admin/properties/new">
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Primeiro Imóvel
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {properties.map((property) => (
            <Card key={property.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Image */}
                  <div className="w-full md:w-40 h-32 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={getPropertyImage(property)}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {property.featured && (
                            <Star className="w-4 h-4 text-brand-gold fill-brand-gold" />
                          )}
                          <h3 className="font-semibold text-lg text-slate-800 truncate">
                            {property.title}
                          </h3>
                          {property.id && (
                            <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 border border-slate-200">
                              {property.id}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mb-2">
                          {property.address?.neighborhood || 'Sem bairro'}, {property.address?.city || 'Toledo'}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge variant="outline">{propertyTypeLabels[property.property_type]}</Badge>
                          <Badge variant="outline">{transactionTypeLabels[property.transaction_type]}</Badge>
                          <Badge className={getStatusColor(property.status)}>
                            {statusLabels[property.status]}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-brand-blue">
                          {formatCurrency(
                            property.transaction_type === 'rent' 
                              ? property.pricing?.rent_price 
                              : property.pricing?.sale_price
                          )}
                        </p>
                        {property.transaction_type === 'rent' && (
                          <p className="text-xs text-slate-500">/mês</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Features */}
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      {property.rooms?.bedrooms > 0 && <span>{property.rooms.bedrooms} quartos</span>}
                      {property.rooms?.bathrooms > 0 && <span>{property.rooms.bathrooms} banheiros</span>}
                      {property.sizes?.built_area_m2 && <span>{property.sizes.built_area_m2}m²</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex md:flex-col gap-2 md:justify-center">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => toggleFeatured(property)}
                      title={property.featured ? 'Remover destaque' : 'Destacar'}
                    >
                      {property.featured ? (
                        <StarOff className="w-4 h-4" />
                      ) : (
                        <Star className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate(`/admin/properties/${property.id}`)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja remover este imóvel? Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(property.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}